(function () {
  'use strict';

  const TEMPLATE_SELECTOR = '[data-video-template]';
  const LOADING_CLASS = 'is-loading';
  const PLAYING_CLASS = 'video-g2-playing';
  const FAILED_CLASS = 'video-g2-autoplay-failed';
  const POSTER_SELECTOR = '.video-g2-poster';

  function getReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isSlowConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn || !conn.effectiveType) return false;
    const type = conn.effectiveType.toLowerCase();
    return type === '2g' || type === 'slow-2g';
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  if (customElements.get('video-gemini2')) return;

  customElements.define('video-gemini2', class VideoGemini2 extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.preloadObserver = null;
      this.playPauseObserver = null;
      this.powerSaverPlay = this.powerSaverPlay.bind(this);
    }

    static get observedAttributes() {
      return ['data-visibility-threshold', 'data-preload-margin', 'data-disable-on-mobile'];
    }

    get visibilityThreshold() {
      const v = parseFloat(this.getAttribute('data-visibility-threshold'), 10);
      return Number.isFinite(v) ? Math.min(1, Math.max(0, v / 100)) : 0.35;
    }

    get preloadMargin() {
      const m = parseInt(this.getAttribute('data-preload-margin'), 10);
      return Number.isFinite(m) ? m : 300;
    }

    get disableOnMobile() {
      return this.getAttribute('data-disable-on-mobile') === 'true' || this.getAttribute('data-disable-on-mobile') === '';
    }

    connectedCallback() {
      this.template = this.querySelector(TEMPLATE_SELECTOR);
      if (!this.template) return;

      if (getReducedMotion() || isSlowConnection() || (this.disableOnMobile && isMobileViewport())) {
        this.setManualPlaybackState();
        return;
      }

      this.startPreloadObserver();
    }

    startPreloadObserver() {
      const rootMargin = this.preloadMargin + 'px';
      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.loadAndAttachVideo();
            this.preloadObserver.unobserve(entry.target);
          });
        },
        { root: null, rootMargin, threshold: 0 }
      );
      this.preloadObserver.observe(this);
    }

    loadAndAttachVideo() {
      const content = this.template.content.cloneNode(true);
      const videoEl = content.querySelector('video');
      if (!videoEl) return;

      videoEl.setAttribute('preload', 'metadata');
      this.appendChild(content);
      this.classList.remove(LOADING_CLASS);
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.setupPlayPauseObserver();
      this.setupPosterCrossfade();
      this.tryPlay();
    }

    setManualPlaybackState() {
      this.classList.remove(LOADING_CLASS);
      this.classList.add(FAILED_CLASS);
      this.addEventListener('click', this.powerSaverPlay, { passive: true });
      this.addEventListener('touchstart', this.powerSaverPlay, { passive: true });
    }

    tryPlay() {
      if (!this.video) return;
      const p = this.video.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          this.classList.remove(FAILED_CLASS);
        }).catch(() => {
          this.classList.add(FAILED_CLASS);
          document.addEventListener('click', this.powerSaverPlay, { passive: true, once: true });
          document.addEventListener('touchstart', this.powerSaverPlay, { passive: true, once: true });
          this.addEventListener('click', this.powerSaverPlay, { passive: true, once: true });
          this.addEventListener('touchstart', this.powerSaverPlay, { passive: true, once: true });
        });
      }
    }

    powerSaverPlay() {
      if (!this.video) {
        if (this.template) this.loadAndAttachVideo();
        return;
      }

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(() => {
          this.classList.remove(FAILED_CLASS);
        }).catch(() => {
          this.classList.add(FAILED_CLASS);
        });
      }
    }

    setupPlayPauseObserver() {
      const threshold = this.visibilityThreshold;
      const pauseThreshold = 0.1;
      const thresholds = [0, pauseThreshold];
      if (threshold > pauseThreshold && threshold < 1) thresholds.push(threshold);
      thresholds.push(1);

      this.playPauseObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const ratio = entry.intersectionRatio;
            if (ratio >= threshold) {
              if (this.video && this.video.paused) this.video.play();
            } else if (ratio < pauseThreshold) {
              if (this.video && !this.video.paused) this.video.pause();
            }
          });
        },
        { root: null, rootMargin: '0px', threshold: thresholds }
      );
      this.playPauseObserver.observe(this);
    }

    setupPosterCrossfade() {
      const poster = this.closest('.video-background') && this.closest('.video-background').querySelector(POSTER_SELECTOR);
      if (!poster) return;
      const onPlaying = () => {
        poster.classList.add(PLAYING_CLASS);
        this.video.removeEventListener('playing', onPlaying);
      };
      this.video.addEventListener('playing', onPlaying);
    }

    disconnectedCallback() {
      if (this.preloadObserver) {
        this.preloadObserver.disconnect();
        this.preloadObserver = null;
      }
      if (this.playPauseObserver) {
        this.playPauseObserver.disconnect();
        this.playPauseObserver = null;
      }
      if (this.video && !this.video.paused) this.video.pause();
      this.removeEventListener('click', this.powerSaverPlay);
      this.removeEventListener('touchstart', this.powerSaverPlay);
      document.removeEventListener('click', this.powerSaverPlay);
      document.removeEventListener('touchstart', this.powerSaverPlay);
    }
  });
})();
